import React from "react";
import { FEATURES } from "../lib/site-data";
import { Users, PenLine, BookOpen, ShieldCheck } from "lucide-react";

const ICONS = { Users, PenLine, BookOpen, ShieldCheck };

export default function Features() {
  const teacherImage = "/illustrations/feature-feedback.png";

  return (
    <section
      id="about"
      className="py-20 lg:py-28 bg-white"
      data-testid="features-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-sm font-medium text-[#D97706] uppercase tracking-widest mb-3">
            Why The Tutor&apos;s Table
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#0F172A] leading-tight">
            A calmer place to learn,
            <br />
            <span className="text-[#1E3A8A]">with everything your child needs.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#475569] leading-relaxed">
            We keep the centre small on purpose. That way teaching stays personal,
            feedback stays specific, and progress is something you can actually
            see at home.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
          {/* Feature 1 - large with image */}
          <div
            className="md:col-span-8 lg:col-span-7 bg-[#FAFAFA] rounded-3xl overflow-hidden border border-[#E2E8F0] group"
            data-testid="feature-card-image"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2">
              <img
                src={teacherImage}
                alt="Cartoon illustration of a tutor giving customised written feedback to a primary school student"
                className="w-full h-64 sm:h-full object-contain bg-white p-4"
              />
              <div className="p-8 sm:p-10 flex flex-col justify-center">
                <div className="w-12 h-12 rounded-2xl bg-[#1E3A8A] text-white flex items-center justify-center mb-5">
                  <PenLine className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-[#0F172A] mb-3">
                  Feedback as personal as the child
                </h3>
                <p className="text-[#475569] leading-relaxed">
                  Every script and homework piece comes back with written notes
                  tailored to the student — what worked, what to fix next, and
                  how to keep improving.
                </p>
              </div>
            </div>
          </div>

          {/* Feature 2 - tall */}
          <div
            className="md:col-span-8 lg:col-span-5 bg-[#1E3A8A] rounded-3xl p-8 sm:p-10 text-white flex flex-col justify-between min-h-[280px]"
            data-testid="feature-card-class-size"
          >
            <div>
              <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center mb-5">
                <Users className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-2xl font-semibold mb-3">
                Max 10 students per class
              </h3>
              <p className="text-white/80 leading-relaxed">
                A deliberately small group so questions get answered, doubts get
                cleared, and no child quietly falls behind.
              </p>
            </div>
            <div className="mt-8 flex items-baseline gap-3">
              <span className="font-heading text-6xl font-bold tracking-tight">
                10
              </span>
              <span className="text-white/70 text-sm">students max</span>
            </div>
          </div>

          {/* Feature 3 */}
          <div
            className="md:col-span-4 lg:col-span-5 bg-white rounded-3xl p-8 border border-[#E2E8F0] hover:border-[#1E3A8A]/40 transition-colors"
            data-testid="feature-card-notes"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#D97706]/10 text-[#D97706] flex items-center justify-center mb-5">
              <BookOpen className="w-6 h-6" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-3">
              Curated, exam-ready notes
            </h3>
            <p className="text-[#475569] leading-relaxed">
              Notes hand-prepared for the MOE syllabus. Concise, well-structured,
              and exactly what your child needs to revise — included at no extra
              cost.
            </p>
          </div>

          {/* Feature 4 - no fees */}
          <div
            className="md:col-span-4 lg:col-span-7 bg-[#FAFAFA] rounded-3xl p-8 sm:p-10 border border-[#E2E8F0]"
            data-testid="feature-card-no-fees"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#1E3A8A]/10 text-[#1E3A8A] flex items-center justify-center mb-5">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-5">
              Transparent pricing — no hidden fees
            </h3>
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: "Material fee", value: "$0" },
                { label: "Deposit", value: "$0" },
                { label: "Registration fee", value: "$0" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="bg-white rounded-2xl p-4 text-center border border-[#E2E8F0]"
                  data-testid={`no-fee-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="font-heading text-2xl sm:text-3xl font-bold text-[#1E3A8A]">
                    {item.value}
                  </div>
                  <div className="text-xs text-[#475569] mt-1">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
